import React, { useState, useEffect } from 'react';
import { X, GraduationCap, Loader2, AlertCircle, Plus, Trash2, GitBranch, Building2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { createFiliereWithCampus, getCampusInfo } from '@/services/filieresService';
import type { CreateFiliereWithCampusData, CampusBasic } from '@/types/filiere.types';

interface FiliereOption {
  code: string;
  name: string;
}

interface CreateFiliereModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateFiliereModal: React.FC<CreateFiliereModalProps> = ({ onClose, onSuccess }) => {
  const { user } = useAuthStore();

  // Get establishment name from user's tenant
  const etablissement = user?.tenantId ? 'Votre Établissement' : '';

  const [formData, setFormData] = useState<CreateFiliereWithCampusData>({
    diplome: '',
    codeFiliere: '',
    filiere: '',
    nombreAnnees: 3,
    anneeAccreditation: new Date().getFullYear(),
  });

  // Options state
  const [hasOptions, setHasOptions] = useState(false);
  const [options, setOptions] = useState<FiliereOption[]>([]);
  const [specialisationStartSemester, setSpecialisationStartSemester] = useState<number | null>(null);

  // Campus state
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [campuses, setCampuses] = useState<CampusBasic[]>([]);
  const [isCommonToAllCampuses, setIsCommonToAllCampuses] = useState(true);
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [loadingCampusInfo, setLoadingCampusInfo] = useState(true);

  // Load campus info on mount
  useEffect(() => {
    const loadCampusInfo = async () => {
      try {
        const info = await getCampusInfo();
        setHasMultipleCampuses(info.hasMultipleCampuses);
        setCampuses(info.campuses);
      } catch (err) {
        console.error('Failed to load campus info:', err);
      } finally {
        setLoadingCampusInfo(false);
      }
    };
    loadCampusInfo();
  }, []);

  // Calculate default specialisation start (first semester of last year)
  const getDefaultSpecialisationStart = (years: number) => (years - 1) * 2 + 1;

  // Generate semester options based on number of years
  const getSemesterOptions = () => {
    const totalSemesters = formData.nombreAnnees * 2;
    return Array.from({ length: totalSemesters }, (_, i) => i + 1);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'nombreAnnees' || name === 'anneeAccreditation' ? parseInt(value, 10) : value,
    }));
  };

  // Add a new empty option
  const addOption = () => {
    setOptions((prev) => [...prev, { code: '', name: '' }]);
  };

  // Remove an option
  const removeOption = (index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  // Update an option
  const updateOption = (index: number, field: 'code' | 'name', value: string) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    );
  };

  // Toggle campus selection
  const toggleCampusSelection = (campusId: string) => {
    setSelectedCampusIds((prev) =>
      prev.includes(campusId) ? prev.filter((id) => id !== campusId) : [...prev, campusId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate campus selection if multiple campuses and not common
    if (hasMultipleCampuses && !isCommonToAllCampuses && selectedCampusIds.length === 0) {
      setError('Veuillez sélectionner au moins un campus');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the base filière first with specialisation start if options are enabled
      const baseData: CreateFiliereWithCampusData = {
        ...formData,
        specialisationStartSemester: hasOptions ? (specialisationStartSemester || getDefaultSpecialisationStart(formData.nombreAnnees)) : null,
        // Add campus assignment only if multiple campuses exist
        ...(hasMultipleCampuses && {
          isCommonToAllCampuses,
          campusIds: isCommonToAllCampuses ? undefined : selectedCampusIds,
        }),
      };
      await createFiliereWithCampus(baseData);

      // If there are options, create each option as a child filière
      if (hasOptions && options.length > 0) {
        for (const option of options) {
          if (option.code && option.name) {
            // Create option with parent code prefix and parent relationship
            const optionCode = `${formData.codeFiliere}_${option.code}`;
            const optionFiliereName = `${formData.filiere} : OPTION ${option.name}`;

            await createFiliereWithCampus({
              ...formData,
              codeFiliere: optionCode,
              filiere: optionFiliereName,
              specialisationStartSemester: null, // Options don't have their own specialisation start
              parentCodeFiliere: formData.codeFiliere, // Link to parent filière
              optionName: option.name, // Store the option name
              // Options inherit same campus settings as parent
              ...(hasMultipleCampuses && {
                isCommonToAllCampuses,
                campusIds: isCommonToAllCampuses ? undefined : selectedCampusIds,
              }),
            });
          }
        }
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Nouvelle Filière</h2>
                <p className="text-sm text-gray-500">Ajouter une filière manuellement</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Établissement (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Établissement
              </label>
              <input
                type="text"
                value={etablissement}
                disabled
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-rempli depuis les informations de l'établissement
              </p>
            </div>

            {/* Diplôme */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diplôme <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="diplome"
                value={formData.diplome}
                onChange={handleChange}
                placeholder="Ex: Licence, Master, Doctorat..."
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Code Filière */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code Filière <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="codeFiliere"
                value={formData.codeFiliere}
                onChange={handleChange}
                placeholder="Ex: 330717L, INFO-L3..."
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Filière (Intitulé) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intitulé de la Filière <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="filiere"
                value={formData.filiere}
                onChange={handleChange}
                placeholder="Ex: Management des organisations..."
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Nombre d'années */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée du Programme <span className="text-red-500">*</span>
              </label>
              <select
                name="nombreAnnees"
                value={formData.nombreAnnees}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n} an{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Année d'accréditation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Année d'Accréditation <span className="text-red-500">*</span>
              </label>
              <select
                name="anneeAccreditation"
                value={formData.anneeAccreditation}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Campus Selection - Only shown when multiple campuses exist */}
            {!loadingCampusInfo && hasMultipleCampuses && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Disponibilité par Campus
                  </span>
                </div>

                <div className="space-y-3">
                  {/* Common to all campuses toggle */}
                  <label className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    <input
                      type="radio"
                      name="campusType"
                      checked={isCommonToAllCampuses}
                      onChange={() => {
                        setIsCommonToAllCampuses(true);
                        setSelectedCampusIds([]);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-blue-800">
                        Commune à tous les campus
                      </span>
                      <p className="text-xs text-blue-600">
                        Cette filière sera disponible sur tous les campus
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="radio"
                      name="campusType"
                      checked={!isCommonToAllCampuses}
                      onChange={() => setIsCommonToAllCampuses(false)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800">
                        Campus spécifiques
                      </span>
                      <p className="text-xs text-gray-600">
                        Sélectionnez les campus où cette filière sera disponible
                      </p>
                    </div>
                  </label>

                  {/* Campus selection checkboxes - only shown when "specific campuses" is selected */}
                  {!isCommonToAllCampuses && (
                    <div className="ml-7 space-y-2 p-3 border border-gray-200 rounded-lg bg-white">
                      {campuses.map((campus) => (
                        <label
                          key={campus.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCampusIds.includes(campus.id)}
                            onChange={() => toggleCampusSelection(campus.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {campus.name}
                            {campus.sigle && (
                              <span className="text-gray-400 ml-1">({campus.sigle})</span>
                            )}
                            {campus.isMainCampus && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                Principal
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                      {selectedCampusIds.length === 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          Sélectionnez au moins un campus
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Options Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasOptions}
                  onChange={(e) => {
                    setHasOptions(e.target.checked);
                    if (!e.target.checked) {
                      setOptions([]);
                      setSpecialisationStartSemester(null);
                    } else {
                      // Set default specialisation start
                      setSpecialisationStartSemester(getDefaultSpecialisationStart(formData.nombreAnnees));
                    }
                  }}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  Cette filière a des options (spécialisations)
                </span>
              </label>

              {hasOptions && (
                <div className="mt-4 space-y-4">
                  {/* Specialisation Start Semester */}
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      À partir de quel semestre commence la spécialisation ? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={specialisationStartSemester || getDefaultSpecialisationStart(formData.nombreAnnees)}
                      onChange={(e) => setSpecialisationStartSemester(parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    >
                      {getSemesterOptions().map((sem) => (
                        <option key={sem} value={sem}>
                          Semestre {sem} (S{sem})
                          {sem === getDefaultSpecialisationStart(formData.nombreAnnees) && ' - défaut'}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-purple-600">
                      Les semestres S1 à S{(specialisationStartSemester || getDefaultSpecialisationStart(formData.nombreAnnees)) - 1} seront communs à toutes les options.
                      À partir de S{specialisationStartSemester || getDefaultSpecialisationStart(formData.nombreAnnees)}, chaque option aura ses propres semestres.
                    </p>
                  </div>

                  <p className="text-xs text-gray-500">
                    Le code de l'option sera: {formData.codeFiliere || '[CODE]'}_[CODE_OPTION]
                  </p>

                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={option.code}
                          onChange={(e) => updateOption(index, 'code', e.target.value)}
                          placeholder="Code (ex: 4490264)"
                          className="px-2 py-1.5 text-sm border border-purple-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <input
                          type="text"
                          value={option.name}
                          onChange={(e) => updateOption(index, 'name', e.target.value)}
                          placeholder="Nom (ex: INNOVATION FINANCIÈRE)"
                          className="px-2 py-1.5 text-sm border border-purple-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addOption}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors w-full justify-center"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter une option
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    Créer la Filière
                    {hasOptions && options.length > 0 && ` + ${options.length} option${options.length > 1 ? 's' : ''}`}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFiliereModal;
